import type { AstroEngine } from "./astro";
import type { Anchor, DayDef } from "./schema";
import { addDays, formatPlainDate, MS_PER_MINUTE, type GeoPoint, type PlainDate } from "./types";

/** The default day for temporal minutes: sea-level sunrise to sea-level sunset (GRA). */
export const GRA_SEA_LEVEL_DAY: DayDef = {
  start: { kind: "sunrise", horizon: "sea_level" },
  end: { kind: "sunset", horizon: "sea_level" },
};

/**
 * Evaluates anchors for one location. Results are cached per date, so a single
 * evaluator can efficiently compute a whole month or year.
 */
export class ZmanimEvaluator {
  private readonly cache = new Map<string, number | null>();

  constructor(
    private readonly engine: AstroEngine,
    readonly point: GeoPoint,
  ) {}

  evaluate(anchor: Anchor, date: PlainDate): number | null {
    const key = `${formatPlainDate(date)}|${JSON.stringify(anchor)}`;
    const cached = this.cache.get(key);
    if (cached !== undefined) return cached;
    const value = this.compute(anchor, date);
    this.cache.set(key, value);
    return value;
  }

  /** Length of one temporal hour (1/12 of the day) in milliseconds. */
  temporalHour(day: DayDef, date: PlainDate): number | null {
    const start = this.evaluate(day.start, date);
    const end = this.evaluate(day.end, date);
    if (start === null || end === null) return null;
    return (end - start) / 12;
  }

  private compute(anchor: Anchor, date: PlainDate): number | null {
    switch (anchor.kind) {
      case "sunrise":
        return this.engine.sunrise(date, this.point, anchor.horizon);
      case "sunset":
        return this.engine.sunset(date, this.point, anchor.horizon);
      case "depression":
        return this.engine.sunAtDepression(date, this.point, anchor.degrees, anchor.dir);
      case "transit":
        return this.engine.solarTransit(date, this.point);
      case "offset": {
        const base = this.evaluate(anchor.from, date);
        if (base === null) return null;
        if (anchor.unit === "fixed") return base + anchor.minutes * MS_PER_MINUTE;
        const hour = this.temporalHour(anchor.zmaniyotDay ?? GRA_SEA_LEVEL_DAY, date);
        if (hour === null) return null;
        return base + (anchor.minutes * hour) / 60;
      }
      case "hours": {
        const start = this.evaluate(anchor.day.start, date);
        const end = this.evaluate(anchor.day.end, date);
        if (start === null || end === null) return null;
        return start + ((end - start) * anchor.hours) / 12;
      }
      case "midpoint": {
        const a = this.evaluate(anchor.a, date);
        const b = this.evaluate(anchor.b, date);
        if (a === null || b === null) return null;
        return (a + b) / 2;
      }
      case "nextDay":
        return this.evaluate(anchor.anchor, addDays(date, 1));
      case "latest":
      case "earliest": {
        const values = anchor.of.map((a) => this.evaluate(a, date));
        if (values.some((v) => v === null)) return null;
        const pick = anchor.kind === "latest" ? Math.max : Math.min;
        return pick(...(values as number[]));
      }
    }
  }
}
